import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Award, BookOpen, Star, Stethoscope } from "lucide-react";
import { MaskedLines, Reveal, Eyebrow } from "@/components/Motion";
import EditorialMarquee from "@/components/EditorialMarquee";
import Marquee from "react-fast-marquee";
import { DOCTOR, EXPERTISE, IMAGES, CASES, REVIEWS } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";
import { useContent } from "@/i18n/useContent";

const Home = () => {
  const { t, lang } = useLang();
  const { expertise: EXPERTISE_L } = useContent();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <main data-testid="home-page">
      {/* ————— HERO ————— */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-screen flex items-center bg-bone overflow-hidden">
        <div className="absolute top-1/3 -left-40 w-[34rem] h-[34rem] rounded-full bg-pine-100/50 blur-3xl pointer-events-none" />

        {/* Full-height edge-to-edge portrait on the right half (desktop) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:block absolute inset-y-0 right-0 w-[46%]"
        >
          <motion.img
            src={IMAGES.portrait}
            alt={`${DOCTOR.name}, Orthopaedic Trauma Surgery Specialist`}
            data-testid="hero-portrait"
            style={{ y: imgY }}
            className="w-full h-full object-cover object-[center_18%] scale-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-bone via-bone/10 to-transparent pointer-events-none" />
        </motion.div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full relative z-10 pt-32 pb-20 lg:py-36">
          <div className="lg:max-w-[52%]">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <Eyebrow>{t("hero_eyebrow")}</Eyebrow>
            </motion.div>

            <MaskedLines
              key={lang}
              lines={[t("hero_line1"), t("hero_line2")]}
              className="mt-8 font-serif font-light text-[11vw] sm:text-6xl lg:text-7xl tracking-tighter leading-[1.05] text-pine-950"
            />

            <motion.p
              data-testid="hero-motto"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="mt-7 font-serif italic text-2xl sm:text-3xl text-pine-500"
            >
              {t("hero_motto")}
            </motion.p>

            <motion.p
              data-testid="hero-tagline"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
              className="mt-3 font-serif text-xl sm:text-2xl text-pine-900"
            >
              {t("hero_tagline")}
            </motion.p>

            <motion.p
              data-testid="hero-subheadline"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 max-w-xl text-sage text-base lg:text-lg leading-relaxed"
            >
              {t("hero_sub", { name: DOCTOR.name, role: DOCTOR.role, hospital: DOCTOR.hospital })}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/book"
                data-testid="hero-book-button"
                className="group bg-pine text-bone font-semibold text-sm tracking-wide px-8 py-4 rounded-full inline-flex items-center gap-2 hover:bg-pine-500 transition-colors duration-300"
              >
                {t("nav_book")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={DOCTOR.phoneHref}
                data-testid="hero-call-button"
                className="font-semibold text-sm tracking-wide px-8 py-4 rounded-full border border-pine-900/20 text-pine-900 hover:border-pine-500 hover:text-pine-500 transition-colors duration-300"
              >
                {t("hero_call", { phone: DOCTOR.phoneDisplay })}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.05 }}
              className="mt-14 flex flex-wrap gap-x-12 gap-y-6"
              data-testid="hero-stats"
            >
              {[
                [`${DOCTOR.experience} Years`, t("stat_exp_label")],
                ["Global Study", t("about_badge1")],
                ["Mumbai", DOCTOR.hospital],
              ].map(([big, small]) => (
                <div key={small} className="border-l-2 border-pine-500/60 pl-4">
                  <div className="font-serif text-2xl text-pine-950">{big}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-sage mt-1">{small}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Full-width portrait below content on mobile */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="lg:hidden mt-14 -mx-6"
          >
            <img
              src={IMAGES.portrait}
              alt={`${DOCTOR.name}, Orthopaedic Trauma Surgery Specialist`}
              className="w-full aspect-[4/5] object-cover object-top"
            />
          </motion.div>
        </div>
      </section>

      <EditorialMarquee dark />

      {/* ————— PHILOSOPHY / MANIFESTO ————— */}
      <section data-testid="philosophy-section" className="bg-bone py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>{t("phil_eyebrow")}</Eyebrow>
            <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl tracking-tight text-pine-950 max-w-2xl leading-[1.05]">
              {t("phil_title1")} <span className="italic text-pine-500">{t("phil_title2")}</span>
            </h2>
          </Reveal>

          <div className="mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
            {[
              { n: "01", title: t("phil1_t"), body: t("phil1_b") },
              { n: "02", title: t("phil2_t"), body: t("phil2_b") },
              { n: "03", title: t("phil3_t"), body: t("phil3_b") },
            ].map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12}>
                <div className="border-t border-pine-900/15 pt-8 group" data-testid={`philosophy-chapter-${c.n}`}>
                  <div className="font-serif italic text-5xl text-pine-300 group-hover:text-pine-500 transition-colors duration-500">
                    {c.n}
                  </div>
                  <h3 className="mt-5 font-serif text-2xl text-pine-950">{c.title}</h3>
                  <p className="mt-3 text-sm text-sage leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— EXPERTISE BENTO ————— */}
      <section data-testid="expertise-section" className="bg-white border-y border-mist py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <Eyebrow>{t("exp_eyebrow")}</Eyebrow>
              <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl tracking-tight text-pine-950 leading-[1.05]">
                {t("exp_title1")}<br />{t("exp_title2")}
              </h2>
            </div>
            <Link
              to="/treatments"
              data-testid="expertise-view-all-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-pine-900 hover:text-pine-500 transition-colors duration-300"
            >
              {t("exp_view_all")}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>

          <Reveal delay={0.1}>
            <div data-testid="case-film-strip" className="mt-14 bg-pine-950 py-8 relative overflow-hidden">
              <div className="absolute top-2.5 inset-x-0 border-t border-dashed border-pine-700/70 pointer-events-none" />
              <div className="absolute bottom-2.5 inset-x-0 border-b border-dashed border-pine-700/70 pointer-events-none" />
              <Marquee speed={28} gradient={false} pauseOnHover>
                {CASES.map((c) => (
                  <div
                    key={c.testId}
                    className="group/frame relative mx-3 my-2 h-72 lg:h-[22rem] w-48 lg:w-60 shrink-0 overflow-hidden bg-pine-900"
                  >
                    <img
                      src={c.src}
                      alt={c.alt}
                      data-testid={c.testId}
                      className="w-full h-full object-cover opacity-90 transition-[opacity,transform] duration-500 group-hover/frame:opacity-100 group-hover/frame:scale-105"
                    />
                    <div className="absolute bottom-3 left-3 right-3">
                      <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-pine-100 bg-pine-950/70 backdrop-blur px-2.5 py-1">
                        {c.caption}
                      </span>
                    </div>
                  </div>
                ))}
              </Marquee>
              <div className="mt-5 px-6 lg:px-10 max-w-7xl mx-auto flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-pine-300">
                  {t("strip_from")}
                </span>
                <span className="text-xs text-pine-200/70 font-serif italic">
                  {t("strip_note")}
                </span>
              </div>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {EXPERTISE_L.slice(0, 3).map((e, i) => (
              <Reveal key={e.title} delay={0.15 + i * 0.1} className="h-full">
                <Link
                  to="/treatments"
                  data-testid={`expertise-card-${i}`}
                  className="group h-full flex flex-col justify-between bg-bone border border-mist p-7 hover:border-pine-500/50 hover:bg-pine-50 transition-colors duration-500"
                >
                  <div>
                    <div className="font-serif italic text-pine-400 text-lg">0{i + 1}</div>
                    <h3 className="mt-2 font-serif text-xl text-pine-950 leading-snug">{e.title}</h3>
                  </div>
                  <ArrowUpRight
                    size={18}
                    className="mt-5 text-pine-400 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-pine-500"
                  />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— ABOUT STRIP ————— */}
      <section data-testid="about-strip-section" className="bg-bone py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-4">
            <div className="overflow-hidden aspect-[3/4] bg-pine-50 flex items-center justify-center">
              <img
                src={IMAGES.portrait}
                alt={DOCTOR.name}
                data-testid="about-strip-portrait"
                className="w-full h-full object-contain object-center"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <Reveal>
              <Eyebrow>{t("about_eyebrow")}</Eyebrow>
              <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl tracking-tight text-pine-950 leading-[1.05]">
                {DOCTOR.name}
              </h2>
              <p className="mt-4 text-sm uppercase tracking-[0.18em] text-pine-500 font-semibold">
                {DOCTOR.role}
              </p>
              <p className="mt-6 text-sage leading-relaxed max-w-2xl">
                {t("about_para")}
              </p>
              <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4 text-sm text-pine-900">
                <span className="inline-flex items-center gap-2">
                  <Award size={16} className="text-pine-500" /> {t("about_badge1")}
                </span>
                <span className="inline-flex items-center gap-2">
                  <BookOpen size={16} className="text-pine-500" /> {t("about_badge2")}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Stethoscope size={16} className="text-pine-500" /> {t("about_badge3")}
                </span>
              </div>
              <Link
                to="/about"
                data-testid="about-strip-link"
                className="group mt-10 inline-flex items-center gap-2 font-semibold text-sm text-pine-900 hover:text-pine-500 transition-colors duration-300"
              >
                {t("about_link")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ————— HOSPITAL ————— */}
      <section data-testid="hospital-section" className="bg-white border-y border-mist py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <Eyebrow>{t("hosp_eyebrow")}</Eyebrow>
              <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl tracking-tight text-pine-950 leading-[1.05]">
                {DOCTOR.hospital}
              </h2>
              <p className="mt-6 text-sage leading-relaxed max-w-xl">
                {t("hosp_para")}
              </p>
              <div className="mt-8 text-sm text-pine-900 font-medium">{DOCTOR.hospitalArea}</div>
              <Link
                to="/book"
                data-testid="hospital-book-button"
                className="group mt-8 inline-flex items-center gap-2 bg-pine text-bone font-semibold text-sm px-8 py-4 rounded-full hover:bg-pine-500 transition-colors duration-300"
              >
                {t("hosp_cta")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-6 order-1 lg:order-2" delay={0.15}>
            <div className="overflow-hidden">
              <img
                src={IMAGES.hospital}
                alt="Modern hospital architecture"
                data-testid="hospital-image"
                className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————— RECOVERY IMAGE + TESTIMONIALS ————— */}
      <section data-testid="stories-section" className="bg-bone py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <Reveal className="lg:col-span-5">
              <div className="relative h-full">
                <div className="overflow-hidden h-full min-h-[24rem]">
                  <img
                    src={IMAGES.running}
                    alt="Patients returning to an active life after recovery"
                    data-testid="recovery-image"
                    className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur px-5 py-4 border border-mist">
                  <div className="font-serif italic text-xl text-pine-950">{t("overlay_quote")}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-sage mt-1">
                    {t("overlay_sub")}
                  </div>
                </div>
              </div>
            </Reveal>
            <div className="lg:col-span-7 flex flex-col justify-center gap-6">
              <Reveal>
                <Eyebrow>{t("stories_eyebrow")}</Eyebrow>
                <div
                  data-testid="google-rating-badge"
                  className="mt-5 inline-flex items-center gap-5 bg-white border border-mist px-6 py-4"
                >
                  <span className="font-serif text-5xl leading-none text-pine-950">5.0</span>
                  <span>
                    <span className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} className="fill-amber-400 text-amber-400" />
                      ))}
                    </span>
                    <span className="block mt-1.5 text-xs uppercase tracking-[0.18em] text-sage">
                      {t("stories_verified", { count: 38 })}
                    </span>
                  </span>
                </div>
              </Reveal>
              {[
                {
                  quote:
                    "In June 2023, my life took an unexpected turn when I met with a severe road accident. Multiple fractures in my right leg, a cut artery, and a fractured hip bone. It was terrifying and painful — but today I am able to walk again.",
                  name: "Vishal Bangard",
                  detail: "Google review · 5 stars",
                },
                {
                  quote:
                    "I was directed to Dr. Lokesh when my dad's two major leg operations had failed. He took over the case and has given him a second chance.",
                  name: "Joylene Lobo",
                  detail: "Google review · 5 stars",
                },
                {
                  quote:
                    "After undergoing multiple surgeries under his supervision, I was pleasantly surprised to be walking comfortably within just 24 hours.",
                  name: "Ram Zawar",
                  detail: "Google review · 5 stars",
                },
              ].map((t, i) => (
                <Reveal key={t.name} delay={0.1 + i * 0.1}>
                  <figure
                    data-testid={`testimonial-${i}`}
                    className="bg-white border border-mist p-8 hover:border-pine-500/40 transition-colors duration-500"
                  >
                    <blockquote className="font-serif text-xl lg:text-2xl text-pine-950 leading-snug">
                      "{t.quote}"
                    </blockquote>
                    <figcaption className="mt-5 flex items-center gap-4">
                      <span className="w-10 h-10 rounded-full bg-pine-100 text-pine-700 font-serif flex items-center justify-center">
                        {t.name[0]}
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-pine-950">{t.name}</span>
                        <span className="block text-xs text-sage">{t.detail}</span>
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>

          {/* REVIEW WALL */}
          <div className="mt-20 lg:mt-24">
            <Reveal className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <h3 className="font-serif font-light text-3xl sm:text-4xl tracking-tight text-pine-950">
                {t("wall_title1")} <span className="italic text-pine-500">{t("wall_title2")}</span>
              </h3>
              <a
                href="https://share.google/5e5Ai8Cs2UaFtKmGx"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="all-reviews-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-pine-900 hover:text-pine-500 transition-colors duration-300"
              >
                {t("wall_link", { count: 38 })}
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10 space-y-5" data-testid="review-wall">
                {[REVIEWS.slice(0, 11), REVIEWS.slice(11)].map((row, rowIdx) => (
                  <Marquee key={rowIdx} speed={28} gradient={false} pauseOnHover direction={rowIdx === 1 ? "right" : "left"}>
                    {row.map((r) => (
                      <div
                        key={r.name}
                        data-testid={`wall-review-${r.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="w-80 shrink-0 bg-white border border-mist p-6 mx-2.5 hover:border-pine-500/40 transition-colors duration-300"
                      >
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} size={11} className="fill-amber-400 text-amber-400" />
                          ))}
                        </div>
                        <p className="mt-3 text-sm text-pine-950 leading-relaxed">"{r.text}"</p>
                        <div className="mt-4 flex items-center justify-between">
                          <span className="text-xs font-semibold text-pine-900 uppercase tracking-[0.14em]">
                            {r.name}
                          </span>
                          <span className="text-[10px] text-sage">{t("google_tag")}</span>
                        </div>
                      </div>
                    ))}
                  </Marquee>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ————— CTA BAND ————— */}
      <section data-testid="cta-band" className="bg-pine-950 text-bone py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute -bottom-32 -right-32 w-[30rem] h-[30rem] rounded-full bg-pine-800/50 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <Reveal>
            <Eyebrow dark>{t("cta_eyebrow")}</Eyebrow>
            <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl">
              {t("cta_title1")} <span className="italic text-pine-300">{t("cta_title2")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/book"
                data-testid="cta-book-button"
                className="group bg-bone text-pine-950 font-semibold text-sm px-8 py-4 rounded-full inline-flex items-center gap-2 hover:bg-pine-300 transition-colors duration-300"
              >
                {t("nav_book")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={DOCTOR.phoneHref}
                data-testid="cta-call-button"
                className="font-semibold text-sm px-8 py-4 rounded-full border border-pine-400/40 text-bone hover:border-pine-300 hover:text-pine-300 transition-colors duration-300"
              >
                {DOCTOR.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
